import { useCallback } from 'react';
import { useAtom } from 'jotai';
import { atomWithImmer } from 'jotai-immer';
import type { CountryCode } from '@cbhq/two-factor-register/types';

export type CardDetailsFormState = {
  addressSearch: string;
  nameOnCard: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  expiration: string;
  state: string;
  phoneNumber: string;
  zipcode: string;
  countryCode: CountryCode;
  email: string;
};

export type CardDetailsFormErrors = {
  addressSearch: string | undefined;
  nameOnCard: string | undefined;
  card: string | undefined;
  expiration: string | undefined;
  cvv: string | undefined;
  zipcode: string | undefined;
  addressLine1: string | undefined;
  city: string | undefined;
  state: string | undefined;
  email: string | undefined;
};

const formErrorsAtom = atomWithImmer<CardDetailsFormErrors>({
  addressSearch: undefined,
  nameOnCard: undefined,
  card: undefined,
  expiration: undefined,
  cvv: undefined,
  zipcode: undefined,
  addressLine1: undefined,
  city: undefined,
  state: undefined,
  email: undefined,
});

const initialFormState: CardDetailsFormState = {
  addressSearch: '',
  nameOnCard: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  expiration: '',
  state: '',
  phoneNumber: '',
  zipcode: '',
  countryCode: 'US',
  email: '',
};

const formStateAtom = atomWithImmer<CardDetailsFormState>(initialFormState);

export const useCardDetailsFormState = () => {
  const [formErrors, setFormErrors] = useAtom(formErrorsAtom);
  const [formState, setFormState] = useAtom(formStateAtom);

  const handleValueChange = useCallback(
    (key: keyof CardDetailsFormState, value: string) => {
      setFormState((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [setFormState],
  );

  const handleMultiValueChange = useCallback(
    (updateFormState: Partial<Record<keyof CardDetailsFormState, string>>) => {
      setFormState((prevState) => ({
        ...prevState,
        ...updateFormState,
      }));
    },
    [setFormState],
  );

  const resetFormState = useCallback(
    () => handleMultiValueChange(initialFormState),
    [handleMultiValueChange],
  );

  const updateFormErrors = useCallback(
    (isValid: boolean, formFieldKey: keyof CardDetailsFormErrors, errorMessage: string) => {
      setFormErrors((oldFormErrors) => ({
        ...oldFormErrors,
        [formFieldKey]: isValid ? undefined : errorMessage,
      }));
    },
    [setFormErrors],
  );

  return {
    formErrors,
    setFormErrors,
    formState,
    handleValueChange,
    resetFormState,
    setFormState,
    updateFormErrors,
    handleMultiValueChange,
  };
};
